/**
 * Kewlioo Fit Finder — scoring engine (standalone, zero deps)
 * ----------------------------------------------------------------
 * Consumes fit-finder-config.json. Drops into a Shopify section <script>,
 * a theme asset, or a Node test. No framework required.
 *
 * Model (improved vs Octane):
 *   1. Gender = HARD filter. Opposite-gender products are removed before
 *      scoring, so there is no cross-gender leakage (Octane used soft -10).
 *   2. Weighted tag scoring. Signal strength lives in the config, not the code.
 *   3. Deterministic ranking: score desc -> priority asc -> title asc.
 *   4. scoreThreshold gates weak/zero-signal products; fallback backfills to N.
 *   5. Every pick carries `reasons` (which answers boosted it) for analytics.
 *
 * Catalog item shape (you supply this array — from Shopify AJAX API, a
 * metafield feed, or hardcoded settings):
 *   { id, handle, title, tags: string[], available?: boolean, priority?: number }
 *   - priority: lower ranks first on ties. Populate from your profit pipeline
 *     (margin desc -> priority asc) for margin-aware tie-breaking.
 *
 * answers shape (from the quiz UI):
 *   { [questionId]: answerId | answerId[] }   // multi-select allowed
 *
 * Main entry:  recommend(config, answers, catalog) -> RankedProduct[]
 */

(function (root, factory) {
  if (typeof module === "object" && module.exports) module.exports = factory();
  else root.FitFinder = factory();
})(typeof self !== "undefined" ? self : this, function () {
  "use strict";

  var norm = function (s) { return String(s == null ? "" : s).trim().toLowerCase(); };

  function tagSet(product) {
    var set = Object.create(null);
    (product.tags || []).forEach(function (t) { set[norm(t)] = true; });
    return set;
  }

  // Resolve which config answer objects the user selected, in question order.
  function selectedAnswers(config, answers) {
    var out = [];
    (config.questions || []).forEach(function (q) {
      var picked = answers[q.id];
      if (picked == null) return; // unanswered / skipped
      var ids = Array.isArray(picked) ? picked : [picked];
      ids.forEach(function (id) {
        var ans = (q.answers || []).filter(function (a) { return a.id === id; })[0];
        if (ans) out.push({ question: q, answer: ans });
      });
    });
    return out;
  }

  // Read the hard gender filter, if a role:gender question was answered.
  function resolveGender(config, answers) {
    var gq = (config.questions || []).filter(function (q) { return q.role === "gender"; })[0];
    if (!gq) return null;
    var picked = answers[gq.id];
    if (picked == null) return null;
    var id = Array.isArray(picked) ? picked[0] : picked;
    var ans = (gq.answers || []).filter(function (a) { return a.id === id; })[0];
    return ans ? (ans.filterValue || null) : null;
  }

  function passesGenderFilter(product, gender, config) {
    if (!gender || (config.settings && config.settings.genderFilter) !== "hard") return true;
    var gTags = config.genderTags || {};
    var tags = tagSet(product);
    // Exclude only if the product is explicitly tagged the OTHER gender.
    var opposite = gender === "male" ? gTags.female : gTags.male;
    if (opposite && tags[norm(opposite)]) return false;
    return true;
  }

  // Does a rule match this product? (tag match or product handle/id/title match)
  function ruleMatches(rule, product, tags) {
    if (rule.tag) return !!tags[norm(rule.tag)];
    if (rule.product) {
      var key = norm(rule.product);
      return norm(product.handle) === key || norm(product.id) === key || norm(product.title) === key;
    }
    return false;
  }

  function scoreProduct(product, sels, config, answers) {
    var tags = tagSet(product);
    var score = 0;
    var reasons = [];

    sels.forEach(function (sel) {
      (sel.answer.rules || []).forEach(function (rule) {
        if (ruleMatches(rule, product, tags)) {
          score += rule.weight;
          reasons.push({ question: sel.question.id, answer: sel.answer.id, weight: rule.weight });
        }
      });
    });

    // Direct product boosts from results.productBoosts, keyed by an answer id
    // that the user actually selected (e.g. full_body -> named sauna suits).
    var boosts = (config.results && config.results.productBoosts) || {};
    var selectedIds = {};
    sels.forEach(function (s) { selectedIds[s.answer.id] = true; });
    Object.keys(boosts).forEach(function (answerId) {
      if (!selectedIds[answerId]) return;
      (boosts[answerId] || []).forEach(function (handle) {
        if (norm(handle) === norm(product.handle) || norm(handle) === norm(product.id)) {
          score += 14;
          reasons.push({ question: "boost", answer: answerId, weight: 14 });
        }
      });
    });

    return { product: product, score: score, reasons: reasons };
  }

  function sortRanked(a, b) {
    if (b.score !== a.score) return b.score - a.score;
    var pa = a.product.priority == null ? Infinity : a.product.priority;
    var pb = b.product.priority == null ? Infinity : b.product.priority;
    if (pa !== pb) return pa - pb;
    return norm(a.product.title) < norm(b.product.title) ? -1 : 1;
  }

  /**
   * recommend(config, answers, catalog) -> RankedProduct[]
   * RankedProduct: { id, handle, title, score, reasons, source: "scored"|"fallback" }
   */
  function recommend(config, answers, catalog) {
    var settings = config.settings || {};
    var n = settings.resultsCount || 3;
    var threshold = settings.scoreThreshold == null ? 1 : settings.scoreThreshold;
    var gender = resolveGender(config, answers);
    var sels = selectedAnswers(config, answers);

    var eligible = (catalog || []).filter(function (p) {
      if (p.available === false) return false;
      return passesGenderFilter(p, gender, config);
    });

    var scored = eligible
      .map(function (p) { return scoreProduct(p, sels, config, answers); })
      .filter(function (r) { return r.score >= threshold; })
      .sort(sortRanked);

    var results = scored.slice(0, n).map(function (r) {
      return {
        id: r.product.id, handle: r.product.handle, title: r.product.title,
        score: r.score, reasons: r.reasons, source: "scored"
      };
    });

    // Backfill from gender-appropriate fallback list if we came up short.
    if (results.length < n) {
      var chosen = {};
      results.forEach(function (r) { chosen[norm(r.handle)] = true; });
      var fb = (config.fallback && config.fallback[gender || "male"]) || [];
      for (var i = 0; i < fb.length && results.length < n; i++) {
        var handle = norm(fb[i]);
        if (chosen[handle]) continue;
        var match = eligible.filter(function (p) { return norm(p.handle) === handle || norm(p.id) === handle; })[0];
        if (!match) continue; // never fabricate a product that isn't in the catalog
        chosen[handle] = true;
        results.push({
          id: match.id, handle: match.handle, title: match.title,
          score: 0, reasons: [], source: "fallback"
        });
      }
    }

    return results;
  }

  /** resolveResultsCopy(config, answers) -> { title, subhead } (goal-personalized) */
  function resolveResultsCopy(config, answers) {
    var r = config.results || {};
    var goalId = answers.goal;
    if (Array.isArray(goalId)) goalId = goalId[0];
    var subhead = (r.subheadByGoal && r.subheadByGoal[goalId]) || r.subheadDefault || "";
    return { title: r.titleText || "", subhead: subhead };
  }

  /**
   * captureAnswers(config, answers) -> { quiz_goal: "Lose Weight & Burn Fat", ... }
   * Flat label map for tracking + cart attributes. Prefix keys with "_" when
   * writing to cart/line-item attributes so they stay hidden from the customer
   * but persist to the Shopify Order (e.g. _quiz_goal). Join order->answers in
   * your Postgres/Metabase pipeline for "which answers convert".
   */
  function captureAnswers(config, answers) {
    var out = {};
    (config.questions || []).forEach(function (q) {
      if (!q.capture) return;
      var picked = answers[q.id];
      if (picked == null) return;
      var ids = Array.isArray(picked) ? picked : [picked];
      var labels = ids.map(function (id) {
        var a = (q.answers || []).filter(function (x) { return x.id === id; })[0];
        return a ? a.label : id;
      });
      out[q.capture] = labels.join(", ");
    });
    return out;
  }

  return {
    recommend: recommend,
    resolveResultsCopy: resolveResultsCopy,
    captureAnswers: captureAnswers,
    resolveGender: resolveGender
  };
});

/* --------------------------------------------------------------------------
 * Example (Node):
 *
 *   const FitFinder = require("./fit-finder-engine.js");
 *   const config = require("./fit-finder-config.json");
 *
 *   const catalog = [
 *     { id:"1", handle:"mens-waist-toner", title:"Men's Heat Trapping Waist Toner",
 *       tags:["Men","Waist Trainer"], priority:1 },
 *     { id:"2", handle:"womens-sauna-jacket", title:"Women's Sauna Jacket",
 *       tags:["Women","Sauna-Suits","Tops"], priority:2 },
 *   ];
 *
 *   const answers = { goal:"lose_weight", body_area:"abdomen_waist", gender:"male",
 *                     features:"sauna" };
 *
 *   console.log(FitFinder.recommend(config, answers, catalog));
 *   console.log(FitFinder.resolveResultsCopy(config, answers));
 *   console.log(FitFinder.captureAnswers(config, answers));
 *     // -> { quiz_goal:"Lose Weight & Burn Fat", quiz_area:"Abdomen & Waist", ... }
 * ------------------------------------------------------------------------ */
